<script lang="ts">
  import { addRoute } from '@integro/core';
  import { gettext, pgettext, ngettext, npgettext } from '@integro/gettext';
  addRoute({
    name: 'meters',
    path: '/meters',
    component: 'MeterBrowser',
    title: () => pgettext('MeterBrowser', 'Meters'),
  });
</script>

<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'MeterBrowser',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const grid = ref(null);

  const $refs = {
    get grid() {
      return grid.value;
    },
  };

  const getQueryParameters = (...args) => {
    if (dsParams.value.actives_only) {
      return { skip_inactive: 'true' };
    }
  };

  const selectedItems = ref([]);
  const dsParams = ref({
    actives_only: true,
  });
  const ds = ref(null);
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'sm',
  }));
  const b_2248226175642056_columnDefaults = computed(() => ({
    sortable: true,
    filterable: true,
    resizable: true,
  }));
  const b_2248226175642056_columns = computed(() => [
    {
      field: 'serial_number',
      name: $fn.value.pgettext('MeterBrowser', 'Serial Number'),
      type: 'command',
      formatter: {
        name: 'routeLink',
        to: 'meter',
        params: {
          id: 'id',
        },
      },
    },
    {
      field: 'main_meter_serial',
      name: $fn.value.pgettext('MeterBrowser', 'Main Meter'),
      formatter: {
        name: 'routeLink',
        to: 'meter',
        navigateOnIconClick: true,
        params: {
          id: 'main_meter_id',
        },
      },
    },
    {
      field: 'inactive',
      name: $fn.value.gettext('Inactive'),
      textAlign: 'center',
      formatter: {
        name: 'checkbox',
      },
    },
    {
      field: 'meter_type',
      name: $fn.value.pgettext('MeterBrowser', 'Meter type'),
      formatter: {
        name: 'gettext',
        context: 'MeterTypes',
        conversion: 'Sentence',
        filterInput: true,
      },
    },
    {
      field: 'tag_id',
      name: $fn.value.pgettext('MeterBrowser', 'Tag ID'),
    },
    {
      field: 'deveui',
      name: $fn.value.pgettext('MeterBrowser', 'DevEUI'),
    },
    {
      field: 'unit',
      name: $fn.value.pgettext('MeterBrowser', 'Unit'),
    },
    {
      field: 'multiplier',
      name: $fn.value.pgettext('MeterBrowser', 'Mt.'),
      type: 'number',
    },
    {
      field: 'site_name',
      name: $fn.value.pgettext('MeterBrowser', 'Site'),
      formatter: {
        name: 'routeLink',
        to: 'property',
        navigateOnIconClick: true,
        params: {
          id: 'site_id',
        },
      },
    },
    {
      field: 'description',
      name: $fn.value.pgettext('MeterBrowser', 'Description'),
    },
    {
      field: 'model_name',
      name: $fn.value.pgettext('MeterBrowser', 'Model'),
    },
    {
      field: 'valid_from',
      name: $fn.value.pgettext('MeterBrowser', 'Valid From'),
      type: 'date',
    },
    {
      field: 'valid_to',
      name: $fn.value.pgettext('MeterBrowser', 'Valid To'),
      type: 'date',
    },
    {
      field: 'groups',
      name: $fn.value.pgettext('MeterBrowser', 'Groups'),
      formatter: {
        name: 'join',
        separator: ', ',
      },
    },
    {
      field: 'rentals',
      name: $fn.value.pgettext('MeterBrowser', 'Rentals'),
      toolTip: $fn.value.pgettext('MeterBrowser', 'Current rentals'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'rental',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'tenants',
      name: $fn.value.pgettext('MeterBrowser', 'Tenants'),
      toolTip: $fn.value.pgettext('MeterBrowser', 'Current tenants'),
      formatter: {
        name: 'chain',
        mode: 'mapReduce',
        separator: ', ',
        formatterContext: 'item',
        formatters: [
          {
            name: 'getvalue',
            opts: {
              field: 'name',
              source: 'value',
            },
          },
          {
            name: 'routeLink',
            to: 'company',
            navigateOnIconClick: true,
            params: {
              id: 'id',
            },
          },
        ],
      },
    },
    {
      field: 'latest_reading_date',
      name: $fn.value.pgettext('MeterBrowser', 'Lr. date'),
      toolTip: $fn.value.pgettext('MeterBrowser', 'Latest reading date'),
      type: 'date',
    },
    {
      field: 'latest_reading_value',
      name: $fn.value.pgettext('MeterBrowser', 'Lr.'),
      toolTip: $fn.value.pgettext('MeterBrowser', 'Latest reading'),
      type: 'number',
    },
    {
      field: 'latest_invoice_reading_date',
      name: $fn.value.pgettext('MeterBrowser', 'L inv. date'),
      toolTip: $fn.value.pgettext(
        'MeterBrowser',
        'Latest invoiced reading date'
      ),
      type: 'date',
    },
    {
      field: 'latest_invoice_reading_value',
      name: $fn.value.pgettext('MeterBrowser', 'L inv. r.'),
      toolTip: $fn.value.pgettext('MeterBrowser', 'Latest invoiced reading'),
      type: 'number',
    },
  ]);

  const $data = computed(() => ({
    selectedItems: selectedItems.value,
    dsParams: dsParams.value,
    ds: ds.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
    b_2248226175642056_columnDefaults: b_2248226175642056_columnDefaults.value,
    b_2248226175642056_columns: b_2248226175642056_columns.value,
  }));

  const $$load_ds = async () => {
    return $fn.value.slickDataSource('list_meter', {
      parameters: getQueryParameters,
    });
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'selectedItems' },
      {
        name: 'dsParams',
        cache: { type: 'local', key: 'MeterBrowser.dsParams' },
      },
      { name: 'ds' },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
      { name: 'b_2248226175642056_columnDefaults', nodeId: '2248226175642056' },
      { name: 'b_2248226175642056_columns', nodeId: '2248226175642056' },
    ],
    loaders: { ds: $$load_ds },
    ux: {},
    dataRefs: {
      selectedItems,
      dsParams,
      ds,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
      b_2248226175642056_columnDefaults,
      b_2248226175642056_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext('MeterBrowser', 'Meters'),
      icon: $config.value.meter.icon,
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_hpa08jvls_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2248226175642056_setData_o2uxiw266_getActionArgs = async (
    $event
  ) => {
    return {
      path: 'selectedItems',
      value: $refs.grid.getSelectedItems(),
      resolve: false,
      noTouchLastLoaded: false,
      noTakeSnapshot: false,
    };
  };
  const act_2095089031469157_showModal_18ib1x8p7_getActionArgs = async (
    $event
  ) => {
    return {
      name: 'CreateMeter',
    };
  };

  const act_7315092382398562_closeModal_hpa08jvls =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_hpa08jvls_getActionArgs,
    });
  const inst_evh_7315092382398562_close_z8n80tgh3 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_hpa08jvls,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_z8n80tgh3],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_5kpjhwz3l =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_sb5e6iz92 =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5kpjhwz3l,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_sb5e6iz92],
    event: 'reload',
  });
  const act_2248226175642056_setData_o2uxiw266 = new actionTypes.SetDataAction({
    actionArgs: act_2248226175642056_setData_o2uxiw266_getActionArgs,
  });
  const inst_evh_2248226175642056_selectedRowsChanged_ik60kaz76 =
    new actionTypes.EventHandlerImpl({
      action: act_2248226175642056_setData_o2uxiw266,
      event: 'selectedRowsChanged',
      displayName: 'setData',
    });
  const inst_evh_2248226175642056_selectedRowsChanged =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2248226175642056_selectedRowsChanged_ik60kaz76],
      event: 'selectedRowsChanged',
    });
  const act_2095089031469157_showModal_18ib1x8p7 =
    new actionTypes.ShowModalAction({
      actionArgs: act_2095089031469157_showModal_18ib1x8p7_getActionArgs,
    });
  const inst_evh_2095089031469157_click_cvjr9t8pg =
    new actionTypes.EventHandlerImpl({
      action: act_2095089031469157_showModal_18ib1x8p7,
      event: 'click',
      displayName: 'showModal',
    });
  const inst_evh_2095089031469157_click = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_2095089031469157_click_cvjr9t8pg],
    event: 'click',
  });

  const actions = {
    inst_evh_7315092382398562_close_z8n80tgh3,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_sb5e6iz92,
    inst_evh_7315092382398562_reload,
    inst_evh_2248226175642056_selectedRowsChanged_ik60kaz76,
    inst_evh_2248226175642056_selectedRowsChanged,
    inst_evh_2095089031469157_click_cvjr9t8pg,
    inst_evh_2095089031469157_click,
  };

  const evh_7315092382398562_close_z8n80tgh3 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_z8n80tgh3.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_sb5e6iz92 = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_sb5e6iz92.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged_ik60kaz76 = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged_ik60kaz76.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2248226175642056_selectedRowsChanged = async (event, scope) => {
    return await actions.inst_evh_2248226175642056_selectedRowsChanged.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click_cvjr9t8pg = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click_cvjr9t8pg.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2095089031469157_click = async (event, scope) => {
    return await actions.inst_evh_2095089031469157_click.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="MeterBrowser"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <VueSlickgrid
      name="meters"
      :headerTitle="pgettext('MeterBrowser', 'Meters')"
      :items="ds"
      checkbox-row-selection
      export-to-csv
      :exportToCsvFilename="pgettext('ExportFilename', 'meters')"
      :columnDefaults="b_2248226175642056_columnDefaults"
      :columns="b_2248226175642056_columns"
      class="page--grid"
      ref="grid"
      @selectedRowsChanged="
        evh_2248226175642056_selectedRowsChanged($event, {})
      "
    >
      <template #toolbar>
        <div class="flex-grow-1 d-flex gap-2 align-items-center">
          <div>
            <ItpFormCheckbox
              name="actives_only"
              :label="pgettext('MeterBrowser', 'Hide inactive meters')"
              inline
              v-model="dsParams.actives_only"
              :value="dsParams.actives_only"
              @input="
                !($event && typeof $event.stopPropagation === 'function') &&
                (dsParams.actives_only =
                  $event && typeof $event === 'object' && 'data' in $event
                    ? $event.data
                    : $event)
              "
            >
            </ItpFormCheckbox>
          </div>
          <ItpButton
            icon="fa-plus"
            :text="pgettext('MeterBrowser', 'New meter')"
            size="sm"
            @click="evh_2095089031469157_click($event, {})"
          >
          </ItpButton>
          <PageHelp path="/meters/meters.html"> </PageHelp>
        </div>
      </template>
    </VueSlickgrid>
  </ItpPage>
</template>
